/**
 * KinsCircle Phase 6B — AI Output Safety Filter
 *
 * STRICTLY ADDITIVE. Runs CLIENT-SIDE on raw AI output BEFORE the user sees it.
 * If the output looks unsafe, the modal shows:
 *   "AI could not safely generate this memory. Try adding a little more detail."
 *
 * Honors STABILITY.md §12.5. NEVER mutates anything; never persists output.
 *
 * The filter is intentionally CONSERVATIVE — false positives (rejecting a fine
 * draft) are acceptable; false negatives (publishing fabricated facts) are not.
 *
 * If the safetyFilter feature flag is OFF, every output passes through.
 */

import { isAISafetyFilterEnabled } from './featureFlag';
import type { MemoryDraftInput } from './promptTemplates';

export type SafetyVerdict =
  | { safe: true }
  | { safe: false; reasons: SafetyReason[]; userMessage: string };

export type SafetyReason =
  | 'fabricated_fact'
  | 'invented_death_or_illness'
  | 'invented_crime'
  | 'invented_military_or_award'
  | 'unknown_person_named'
  | 'medical_advice'
  | 'political_content'
  | 'explicit_or_hateful'
  | 'overdramatic'
  | 'too_short'
  | 'empty';

const FABRICATED_FACT_PATTERNS: { rx: RegExp; reason: SafetyReason }[] = [
  // Invented death / illness
  { rx: /\b(passed away|died|deceased|funeral|cancer|terminal|diagnosed with)\b/i, reason: 'invented_death_or_illness' },
  // Invented crime
  { rx: /\b(arrested|imprisoned|jail|prison|convicted|crime|criminal record|murder|assault)\b/i, reason: 'invented_crime' },
  // Invented military / awards
  { rx: /\b(medal of honor|purple heart|silver star|bronze star|nobel prize|served in (the )?(army|navy|marines|air force)|combat veteran|deployed to)\b/i, reason: 'invented_military_or_award' },
  // Medical
  { rx: /\b(prescribed|dosage|mg of|treatment for|chemo(therapy)?|surgery for|tumor)\b/i, reason: 'medical_advice' },
  // Political
  { rx: /\b(republican|democrat|liberal|conservative|trump|biden|election fraud|antifa|maga)\b/i, reason: 'political_content' },
  // Explicit / hateful
  { rx: /\b(slur|racist|nazi|kkk|sex(ual)? (act|encounter)|nude)\b/i, reason: 'explicit_or_hateful' },
];

const OVERDRAMATIC_PATTERNS: RegExp[] = [
  /\b(legendary|mythic|immortal|destined|fated|the entire universe|forever changed history|chosen one)\b/i,
  /!{2,}/,
];

const FACT_CLAIM_PATTERNS: RegExp[] = [
  /\bborn in \d{4}\b/i,
  /\bin \d{4},?\s+(?:he|she|they)\b/i,
  /\bgraduated from\b/i,
  /\bworked at\b/i,
  /\bmarried in \d{4}\b/i,
  /\bfounded\b/i,
  /\bowned (?:a|the)\b/i,
];

const stripPunct = (s: string): string =>
  s.toLowerCase().replace(/[^a-z\s]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Returns the set of "first-name tokens" that the user explicitly provided
 * (taggedNames + author + caption). The author's name is included so the AI
 * can refer to "I" or to the author by name without tripping the filter.
 */
const buildAllowedNames = (input: MemoryDraftInput): Set<string> => {
  const out = new Set<string>();
  const push = (raw: string | undefined | null) => {
    if (!raw) return;
    stripPunct(raw)
      .split(' ')
      .filter((w) => w.length >= 3)
      .forEach((w) => out.add(w));
  };
  (input.taggedNames ?? []).forEach((n) => push(n));
  push(input.authorName);
  // Caption-named people are implicitly allowed (the user wrote them).
  push(input.caption);
  // Always allow these generic relational terms.
  [
    'mom', 'mum', 'dad', 'mother', 'father', 'parent', 'parents',
    'son', 'daughter', 'child', 'children', 'kid', 'kids',
    'brother', 'sister', 'sibling', 'siblings',
    'grandma', 'grandpa', 'grandmother', 'grandfather', 'nana', 'papa',
    'uncle', 'aunt', 'cousin', 'nephew', 'niece',
    'family', 'we', 'us', 'they', 'everyone', 'kids', 'love',
    'husband', 'wife', 'spouse', 'partner',
    'friend', 'friends',
  ].forEach((w) => out.add(w));
  return out;
};

const findUnknownNames = (story: string, allowed: Set<string>): string[] => {
  // Heuristic: capitalized two-letter+ words that look like first names
  // (excluding sentence-start words). We only flag names NOT in `allowed`.
  const out: string[] = [];
  const tokens = story.split(/\s+/);
  tokens.forEach((tok, i) => {
    const stripped = tok.replace(/[^A-Za-z'-]/g, '');
    if (stripped.length < 3) return;
    // Must start uppercase
    if (stripped[0] !== stripped[0].toUpperCase()) return;
    // Skip first word of a sentence (preceded by . ? !)
    const prev = i > 0 ? tokens[i - 1] : '';
    if (i === 0 || /[.!?]$/.test(prev)) return;
    // Skip all-caps acronyms
    if (stripped === stripped.toUpperCase()) return;
    const lower = stripped.toLowerCase();
    if (allowed.has(lower)) return;
    // Skip months/days
    if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/i.test(lower)) return;
    out.push(stripped);
  });
  return Array.from(new Set(out)).slice(0, 5);
};

/**
 * Validate an AI-generated story against the input context.
 * Returns `{ safe: true }` if every reason is clear.
 */
export const validateAIStory = (
  story: string,
  input: MemoryDraftInput,
): SafetyVerdict => {
  if (!isAISafetyFilterEnabled()) return { safe: true };

  const reasons: SafetyReason[] = [];

  if (!story || !story.trim()) reasons.push('empty');
  else if (story.trim().length < 25) reasons.push('too_short');

  // Pattern-based checks
  for (const { rx, reason } of FABRICATED_FACT_PATTERNS) {
    if (rx.test(story)) reasons.push(reason);
  }

  // Overdramatic
  if (OVERDRAMATIC_PATTERNS.some((rx) => rx.test(story))) {
    reasons.push('overdramatic');
  }

  // Fact claims that aren't anchored in the input
  const captionLow = (input.caption ?? '').toLowerCase();
  const dateLow = (input.date ?? '').toLowerCase();
  const factHits = FACT_CLAIM_PATTERNS.filter((rx) => rx.test(story));
  if (factHits.length > 0) {
    const anyAnchored = factHits.some((rx) => {
      const m = story.match(rx);
      if (!m) return false;
      const claim = m[0].toLowerCase();
      return captionLow.includes(claim) || dateLow.includes(claim);
    });
    if (!anyAnchored) reasons.push('fabricated_fact');
  }

  // Unknown people named
  const allowed = buildAllowedNames(input);
  const unknowns = findUnknownNames(story, allowed);
  if (unknowns.length > 0) reasons.push('unknown_person_named');

  if (reasons.length === 0) return { safe: true };

  return {
    safe: false,
    reasons: Array.from(new Set(reasons)),
    userMessage:
      'AI could not safely generate this memory. Try adding a little more detail.',
  };
};

/** Convenience: validate a generic AI text (timeline / OTD recap). */
export const validateAIText = (text: string): SafetyVerdict => {
  if (!isAISafetyFilterEnabled()) return { safe: true };
  const reasons: SafetyReason[] = [];
  if (!text || !text.trim()) reasons.push('empty');
  for (const { rx, reason } of FABRICATED_FACT_PATTERNS) {
    if (rx.test(text)) reasons.push(reason);
  }
  if (OVERDRAMATIC_PATTERNS.some((rx) => rx.test(text))) {
    reasons.push('overdramatic');
  }
  if (reasons.length === 0) return { safe: true };
  return {
    safe: false,
    reasons: Array.from(new Set(reasons)),
    userMessage:
      'AI could not safely generate this summary. Try adding a little more detail.',
  };
};
